/**
 * Coming back from Whop's card checkout.
 *
 * `redirect_url` on the setup checkout is `…/?vault=return`, and for a while
 * nothing read it: the return landed on a page that behaved as though nothing
 * had happened. In the intended flow the checkout opens in a second tab, so the
 * customer is expected to find their way back to the first tab and press "I've
 * added my card" - the return tab is scenery. But anyone whose browser reused
 * the same tab, or who finished on their phone, had no way to tell whether the
 * card had saved at all. It HAS saved, at Whop, which is the worst version of
 * not knowing.
 *
 * So it resolves on whichever tab comes back, and says so.
 *
 * @module web/state/vault
 */
import { get, raw } from "../api/client.ts";
import type { VaultSetupResponse } from "../api/types.ts";

export interface VaultReturn {
  ok: boolean;
  message: string;
}

/**
 * Ask where to send someone to put a card on file.
 *
 * The card is vaulted at Whop and we never see it, so "add a card" is a
 * hand-off to their hosted checkout and then a lookup on the way back. The
 * server reuses an existing setup checkout rather than minting a new one, so
 * calling this twice is safe - see the note on the route.
 */
export function startVault(): Promise<VaultSetupResponse> {
  return get<VaultSetupResponse>("/api/billing/vault");
}

/** Whether this page load is a return from the card checkout. */
export function isVaultReturn(search: string): boolean {
  return new URLSearchParams(search).get("vault") === "return";
}

/**
 * Resolve the card, and report what to tell the customer.
 *
 * `raw` rather than `get`, deliberately: this runs on a page that has just
 * authenticated, and a hiccup here must not throw the customer back to sign-in
 * when their card may well have saved.
 *
 * And it must not throw AT ALL, which is what the try is for. `raw` does not
 * reject on a status, but `fetch` itself rejects when the request never gets
 * that far - offline, DNS, a dropped connection. Neither caller was ready for
 * that, and both are the card rail:
 *
 *   - the page-load path in BrandContext runs inside a `void (async () => ...)`,
 *     so a rejection there is an unhandled rejection and `clearVaultParam()` and
 *     the notice never run. The customer comes back from Whop, is told nothing
 *     at all, and `?vault=return` stays in the address bar.
 *   - the "I've added my card" button awaits this between `setBusy(true)` and
 *     `setBusy(false)`, so a rejection leaves it disabled and reading "Checking…"
 *     with no way out but a reload.
 *
 * This function's whole contract is that it returns an outcome, so a transport
 * failure is one of the outcomes rather than an exception. Both callers are then
 * correct as written.
 */
export async function resolveVaultReturn(): Promise<VaultReturn> {
  let res: { status: number; body: { message?: string } | null };
  try {
    res = await raw<{ message?: string }>("/api/billing/vault/resolve", { method: "POST" });
  } catch {
    // The card may well have saved at Whop - this says nothing either way, and
    // must not imply it failed. Tapping again is safe: the lookup is a read.
    return { ok: false, message: "We could not reach us just now - try again in a moment." };
  }
  if (res.status === 200) {
    return { ok: true, message: "Card saved - ordering is one tap from here." };
  }
  return { ok: false, message: res.body?.message ?? "We could not confirm the card yet." };
}

/** Drop `?vault=` from the address bar, keeping everything else intact. */
export function clearVaultParam(): void {
  const u = new URL(window.location.href);
  u.searchParams.delete("vault");
  window.history.replaceState(null, "", u.pathname + u.search + u.hash);
}
